import React from "react";
import { Editor } from "@tinymce/tinymce-react";
import { isVideo, FILE_MEDIA_TYPES, PERIODS, FORM_PERIODS, MEDIA_LABELS } from "./broadcastFormHelpers";

/**
 * The Content / Media / Timing / Behaviour sections of the publisher form. Each receives the
 * Formik `values` and `setFieldValue`; the container owns submit, preview and the audience.
 */

export const Text = ({ id, label, value, onChange, type = "text", disabled, placeholder, title }) => (
  <div className="form-group">
    <label htmlFor={id}>{label}</label>
    <input type={type} className="form-control" id={id} value={value} disabled={disabled}
      placeholder={placeholder} title={title} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export const Check = ({ id, label, checked, onChange, disabled, title }) => (
  <div className="form-check">
    <input type="checkbox" className="form-check-input" id={id} checked={!!checked} disabled={disabled} title={title}
      onChange={(e) => onChange(e.target.checked)} />
    <label className="form-check-label" htmlFor={id}>{label}</label>
  </div>
);

export const Select = ({ id, label, value, onChange, options, disabled }) => (
  <div className="form-group">
    <label htmlFor={id}>{label}</label>
    <select className="form-control" id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
    </select>
  </div>
);

// Same TinyMCE set-up as the old HR announcement form, with a reduced toolbar: the server
// keeps only p, br, b/strong, i/em, u, s, lists, h2-h4, blockquote, a, img, span[style], table.
const EDITOR_INIT = {
  height: 300,
  menubar: false,
  plugins: ['advlist', 'autolink', 'lists', 'link', 'table'],
  toolbar: 'undo redo | blocks | bold italic underline strikethrough forecolor | alignleft aligncenter alignright | link | bullist numlist | removeformat',
  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
};

// Switching kind also moves the period: a form repeats per period (never "none"), a notice
// starts from "none" again; a custom item keeps whatever period it has (the seeds use once / year).
const setKind = (values, set, kind) => {
  set("kind", kind);
  if (kind === "form" && values.period === "none") set("period", "once");
  if (kind === "notice") set("period", "none");
};

const KINDS = [["notice", "Notice"], ["form", "Form"], ["custom", "Custom"]];
const CUSTOM_HR_ONLY = "Only HR can post this custom form";

// The registry keys as GET /broadcasts/lookups lists them (server BroadcastController.php:114-116:
// [{key, label, hr_only}] in config/broadcasts.php order). An hr_only key is offered but disabled
// for a department head — the server answers 403 for it anyway (BroadcastRequest.php:43).
export const CustomFormSelect = ({ value, onChange, customForms, isHr }) => (
  <div className="form-group">
    <label htmlFor="custom-key">Custom form</label>
    <select className="form-control" id="custom-key" value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose a custom form</option>
      {(customForms || []).map((f) => (
        <option key={f.key} value={f.key} disabled={!isHr && !!f.hr_only} title={!isHr && f.hr_only ? CUSTOM_HR_ONLY : undefined}>
          {f.label}
        </option>
      ))}
    </select>
    <small className="form-text text-muted">One active item per custom form; content below is optional for a custom item.</small>
  </div>
);

export const ContentSection = ({ values, set, customForms, isHr }) => (
  <>
    <h4>Content</h4>
    <div className="form-group">
      {KINDS.map(([kind, text]) => (
        <div className="form-check form-check-inline" key={kind}>
          <input type="radio" className="form-check-input" id={"kind-" + kind} name="kind" value={kind}
            checked={values.kind === kind} onChange={() => setKind(values, set, kind)} />
          <label className="form-check-label" htmlFor={"kind-" + kind}>{text}</label>
        </div>
      ))}
    </div>
    {values.kind === "custom" ? (
      <CustomFormSelect value={values.custom_key} onChange={(v) => set("custom_key", v)} customForms={customForms} isHr={isHr} />
    ) : null}
    <Text id="title" label="Title" value={values.title} onChange={(v) => set("title", v)} />
    <Text id="headline" label="Headline" value={values.headline} onChange={(v) => set("headline", v)} />
    <div className="form-group">
      <Editor
        tinymceScriptSrc='https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.3.1/tinymce.min.js'
        licenseKey='gpl'
        textareaName="content"
        initialValue={values.content || ''}
        onEditorChange={(html) => set("content", html)}
        init={EDITOR_INIT}
      />
    </div>
    <div className="form-group">
      <label htmlFor="thumbnail">Thumbnail</label>
      <input type="file" className="form-control-file" id="thumbnail" accept=".jpg,.jpeg,.png,.webp"
        onChange={(e) => set("thumbnail", e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
      <small className="form-text text-muted">Card image on the Announcements tab (JPG, PNG or WebP).</small>
    </div>
    <div className="form-group">
      <Check id="display-popup" label="Pop-up" checked={values.display_popup} onChange={(v) => set("display_popup", v)} />
      <Check id="display-tab" label="Announcements tab" checked={values.display_tab} onChange={(v) => set("display_tab", v)} />
    </div>
    <Check id="on-link" label="Card opens a link instead of the archive page" checked={values.on_link} onChange={(v) => set("on_link", v)} />
    <Text id="link" label="Link" value={values.link} onChange={(v) => set("link", v)} disabled={!values.on_link} placeholder="https://" />
    <div className="form-row">
      <div className="col-md-4"><Text id="cta-label" label="CTA label" value={values.cta_label} onChange={(v) => set("cta_label", v)} /></div>
      <div className="col-md-8"><Text id="cta-url" label="CTA URL" value={values.cta_url} onChange={(v) => set("cta_url", v)} placeholder="https://" /></div>
    </div>
  </>
);

export const MediaSection = ({ values, set, lookups, onFile, limits }) => (
  <>
    <h4>Media</h4>
    {values.media_existing ? (
      // Edit: the stored variant stays unless a new file is chosen / the URL is changed (nothing is re-sent otherwise).
      <p className="form-text text-muted broadcast-media-current">
        Current media: {MEDIA_LABELS[values.media_existing.media_type] || values.media_existing.media_type} ({values.media_existing.url}).
        Choose a new file or change the URL to replace it.
      </p>
    ) : null}
    <Select id="media-type" label="Media type" value={values.media_type} onChange={(v) => set("media_type", v)}
      options={[["", "None"], ["image", "Image"], ["pdf", "PDF"], ["video_file", "Video file"], ["video_link", "Video link (YouTube / Vimeo)"]]} />
    <div className="form-group">
      <label htmlFor="media-file">Media file</label>
      <input type="file" className="form-control-file" id="media-file" disabled={!FILE_MEDIA_TYPES.includes(values.media_type)}
        onChange={(e) => onFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
      <small className="form-text text-muted">
        Image up to {limits.image_mb} MB, PDF up to {limits.pdf_mb} MB, MP4 / WebM up to {limits.video_mb} MB.
      </small>
    </div>
    <Text id="media-url" label="Media URL" value={values.media_url} onChange={(v) => set("media_url", v)}
      disabled={values.media_type !== "video_link"} placeholder="https://youtu.be/…" />
    <Select id="media-country" label="Variant country" value={values.media_country_id} onChange={(v) => set("media_country_id", v)}
      options={[["0", "Default (all countries)"]].concat((lookups.countries || []).map((c) => [String(c.id), c.name]))} />
  </>
);

export const TimingSection = ({ values, set, timezones }) => (
  <>
    <h4>Timing</h4>
    <Select id="timezone" label="Timezone" value={values.timezone} onChange={(v) => set("timezone", v)}
      options={timezones.map((tz) => [tz, tz])} />
    <div className="form-row">
      <div className="col-md-4"><Text id="starts-at" label="Starts at" value={values.starts_at} onChange={(v) => set("starts_at", v)} placeholder="YYYY-MM-DD HH:mm" /></div>
      <div className="col-md-4"><Text id="ends-at" label="Ends at" value={values.ends_at} onChange={(v) => set("ends_at", v)} placeholder="YYYY-MM-DD HH:mm" /></div>
    </div>
    <Check id="repeat" label="Repeat monthly" checked={values.repeat} onChange={(v) => set("repeat", v)} />
    <div className="form-row">
      <div className="col-md-2"><Text id="repeat-from" label="From day" type="number" value={values.repeat_from} onChange={(v) => set("repeat_from", v)} disabled={!values.repeat} /></div>
      <div className="col-md-2"><Text id="repeat-to" label="To day" type="number" value={values.repeat_to} onChange={(v) => set("repeat_to", v)} disabled={!values.repeat} /></div>
    </div>
    <Select id="period" label="Period" value={values.period} onChange={(v) => set("period", v)}
      options={values.kind === "form" ? FORM_PERIODS : PERIODS} disabled={values.kind === "form" && !!values.per_person} />
    {values.kind === "form" && values.per_person ? (
      <small className="form-text text-muted">A per-person form runs once per document.</small>
    ) : null}
  </>
);

const HR_ONLY = "Only HR can make an item mandatory";

// SPEC 23.2: per_person forces period "once", no anonymous answers and the pop-up display (the
// server answers 422 otherwise); a signed form cannot be anonymous either.
export const PER_PERSON_LABEL = "One document per person (HR raises it for a named employee)";
const setPerPerson = (set, checked) => {
  set("per_person", checked);
  if (checked) {
    set("period", "once");
    set("anonymous", false);
    set("display_popup", true);
  }
};

const FormOptions = ({ values, set }) => {
  const noAnonymous = !!values.per_person || !!values.require_signature;
  return (
    <>
      <Check id="per-person" label={PER_PERSON_LABEL} checked={values.per_person} onChange={(v) => setPerPerson(set, v)} />
      <Check id="require-signature" label="Require signature" checked={values.require_signature}
        onChange={(v) => { set("require_signature", v); if (v) set("anonymous", false); }} />
      <Check id="anonymous" label="Anonymous responses" checked={values.anonymous && !noAnonymous} disabled={noAnonymous}
        title={noAnonymous ? "Not available for per-person or signed forms" : undefined} onChange={(v) => set("anonymous", v)} />
    </>
  );
};

export const BehaviourSection = ({ values, set, isHr }) => {
  const mandatory = values.behaviour === "mandatory";
  const video = isVideo(values.media_type);
  return (
    <>
      <h4>Behaviour</h4>
      <div className="form-group">
        <div className="form-check form-check-inline">
          <input type="radio" className="form-check-input" id="behaviour-closable" name="behaviour" value="closable"
            checked={!mandatory} onChange={() => set("behaviour", "closable")} />
          <label className="form-check-label" htmlFor="behaviour-closable">Closable</label>
        </div>
        <div className="form-check form-check-inline">
          <input type="radio" className="form-check-input" id="behaviour-mandatory" name="behaviour" value="mandatory"
            checked={mandatory} disabled={!isHr} title={isHr ? undefined : HR_ONLY} onChange={() => set("behaviour", "mandatory")} />
          <label className="form-check-label" htmlFor="behaviour-mandatory" title={isHr ? undefined : HR_ONLY}>Mandatory</label>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-4">
          <Select id="reshow-rule" label="Re-show rule" value={values.reshow_rule} onChange={(v) => set("reshow_rule", v)} disabled={mandatory}
            options={[["always", "Every dashboard load"], ["next_login", "Next login after a close"], ["daily", "Once a day"], ["never", "Never after a close"]]} />
        </div>
        <div className="col-md-4">
          <Text id="escalate-after" label="Escalate after (days)" type="number" value={values.escalate_after_days}
            onChange={(v) => set("escalate_after_days", v)} disabled={!isHr || mandatory} title={isHr ? undefined : HR_ONLY} />
        </div>
        <div className="col-md-4">
          <Text id="priority" label="Priority" type="number" value={values.priority} onChange={(v) => set("priority", v)} />
        </div>
      </div>
      <Check id="require-ack" label="Require acknowledgement" checked={values.require_ack} onChange={(v) => set("require_ack", v)} />
      <Text id="ack-text" label="Acknowledgement text" value={values.ack_text} onChange={(v) => set("ack_text", v)} disabled={!values.require_ack}
        placeholder="I have read and understood this" />
      <Check id="confirm-before-submit" label="Confirm before submit" checked={values.confirm_before_submit} onChange={(v) => set("confirm_before_submit", v)} />
      <Text id="after-submit-url" label="After-submit URL" value={values.after_submit_url} onChange={(v) => set("after_submit_url", v)} placeholder="https://" />
      <Check id="must-watch" label="Must watch" checked={video && values.must_watch} disabled={!video} onChange={(v) => set("must_watch", v)}
        title={video ? undefined : "Available for video media only"} />
      {values.kind === "form" ? <FormOptions values={values} set={set} /> : null}
      <Check id="notify-bell" label="Notify bell" checked={values.notify_bell} onChange={(v) => set("notify_bell", v)} />
      <Check id="status" label="Active" checked={values.status} onChange={(v) => set("status", v)} />
    </>
  );
};
